import { DocumentListItemDto, Status } from 'services/api/api-client.types.ts';
import { VariableDocumentList } from '../../../../../variables/components/variableDocumentsList/VariableDocumentsList.tsx';
import { useSelectModeContext } from '../../useSelectModeContext.tsx';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { useCallback } from 'react';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import styles from './BulkUpdateStatusModal.module.scss';

export enum UpdateFieldType {
  Status = 'status',
  Version = 'version',
  Date = 'date',
}

type useConfirmSigningDocumentsBulkUpdateProps = {
  afterConfirm: (updatedFieldType: UpdateFieldType) => void;
  revisionDocuments: DocumentListItemDto[];
  projectId: number;
  revisionId: number;
};

export const useConfirmSigningDocumentsBulkUpdate = (
  props: useConfirmSigningDocumentsBulkUpdateProps,
) => {
  const modals = useModal();
  const i18n = useScopedTranslation('Component.ConfirmBulkUpdateModal');
  const { store } = useSelectModeContext();

  const getNonSigningDocuments = () =>
    store
      .getSelectedFiles()
      .filter(
        (d) =>
          !props.revisionDocuments.some(
            (rd) => rd.id === d.documentId && rd.status === Status.Signing,
          ),
      );

  const onBulkUpdateWithConfirm = useCallback(
    async (updatedFieldType: UpdateFieldType) => {
      if (!props.revisionDocuments) return;

      const selectedDocuments = store.getSelectedFiles();

      const signingDocuments = selectedDocuments.filter((d) =>
        props.revisionDocuments.some(
          (rd) => rd.id === d.documentId && rd.status === Status.Signing,
        ),
      );

      if (signingDocuments.length > 0) {
        const result = await modals.showConfirm({
          title: i18n.t('title'),
          text: (
            <div className={styles.confirmModalBody}>
              <div>{i18n.t('top_text', { fieldName: updatedFieldType })}</div>
              <VariableDocumentList
                documents={signingDocuments.map((d) => ({
                  id: d.documentId!,
                  name: d.path.split('/').pop() ?? '',
                  projectId: props.projectId,
                  revisionId: props.revisionId,
                  // Can be null here because this is confirmation in scope of current revision
                  revisionVersion: null,
                }))}
              />
              <div>
                {i18n.t('bottom_text', { fieldName: updatedFieldType })}
              </div>
            </div>
          ),
          okButtonText: i18n.t('ok_button'),
          cancelButtonText: i18n.t('cancel_button'),
        });
        if (!result) return;
      }

      props.afterConfirm(updatedFieldType);
    },
    [store, modals, props.revisionDocuments, props.afterConfirm],
  );

  return { onBulkUpdateWithConfirm, getNonSigningDocuments };
};
